import EMOJI_SHORTCODES from "markdown-it-emoji/lib/data/full.mjs";
import type { Delimiter, MarkdownIt, StateBlock, StateCore, StateInline, Token } from "markdown-it";

const BACKSLASH = 0x5c;
const DOLLAR = 0x24;
const COLON = 0x3a;
const EQUALS = 0x3d;
const BANG = 0x21;

function isAlphaNumeric(code: number): boolean {
  return (
    (code >= 0x30 && code <= 0x39) ||
    (code >= 0x41 && code <= 0x5a) ||
    (code >= 0x61 && code <= 0x7a)
  );
}

/**
 * Generic two-character paired inline delimiter, used for `==highlight==` and
 * `!!placeholder!!`. Built on markdown-it's public delimiter machinery so that
 * the pairs nest correctly with emphasis.
 */
function pairedDelimiter(md: MarkdownIt, name: string, tag: string, marker: string): void {
  const code = marker.charCodeAt(0);
  const pair = marker + marker;

  function tokenize(state: StateInline, silent: boolean): boolean {
    if (silent) return false;
    if (state.src.charCodeAt(state.pos) !== code) return false;

    const scanned = state.scanDelims(state.pos, true);
    let len = scanned.length;
    if (len < 2) return false;

    if (len % 2 !== 0) {
      const token = state.push("text", "", 0);
      token.content = marker;
      len -= 1;
    }
    for (let i = 0; i < len; i += 2) {
      const token = state.push("text", "", 0);
      token.content = pair;
      state.delimiters.push({
        marker: code,
        length: 0,
        jump: i / 2,
        token: state.tokens.length - 1,
        end: -1,
        open: scanned.can_open,
        close: scanned.can_close,
      });
    }
    state.pos += scanned.length;
    return true;
  }

  function pair_(state: StateInline, delimiters: Delimiter[]): void {
    for (const startDelim of delimiters) {
      if (startDelim.marker !== code) continue;
      if (startDelim.end === -1) continue;
      const endDelim = delimiters[startDelim.end];
      if (!endDelim) continue;

      const open = state.tokens[startDelim.token];
      const close = state.tokens[endDelim.token];
      if (!open || !close) continue;

      open.type = `${name}_open`;
      open.tag = tag;
      open.nesting = 1;
      open.markup = pair;
      open.content = "";

      close.type = `${name}_close`;
      close.tag = tag;
      close.nesting = -1;
      close.markup = pair;
      close.content = "";
    }
  }

  md.inline.ruler.before("emphasis", name, tokenize);
  md.inline.ruler2.before("emphasis", name, (state: StateInline) => {
    pair_(state, state.delimiters);
    for (const meta of state.tokens_meta) {
      if (meta?.delimiters) pair_(state, meta.delimiters);
    }
    return true;
  });
}

/** `==text==` */
export function highlightPlugin(md: MarkdownIt): void {
  pairedDelimiter(md, "highlight", "mark", String.fromCharCode(EQUALS));
}

/** `!!text!!` */
export function placeholderPlugin(md: MarkdownIt): void {
  pairedDelimiter(md, "placeholder", "span", String.fromCharCode(BANG));
}

/**
 * `$x$` inline math. The closing `$` may not be followed by a digit so that
 * currency runs such as `$5 and $6` stay plain text. `$$x$$` written inline is
 * accepted as well because it is the editor's typing trigger.
 */
export function inlineMathPlugin(md: MarkdownIt): void {
  function rule(state: StateInline, silent: boolean): boolean {
    const start = state.pos;
    if (state.src.charCodeAt(start) !== DOLLAR) return false;

    const doubled = state.src.charCodeAt(start + 1) === DOLLAR;
    const markerLength = doubled ? 2 : 1;
    let pos = start + markerLength;
    if (pos >= state.posMax) return false;
    // An opening delimiter followed by whitespace is not math.
    if (/\s/.test(state.src.charAt(pos))) return false;

    let end = -1;
    while (pos < state.posMax) {
      const ch = state.src.charCodeAt(pos);
      if (ch === BACKSLASH) {
        pos += 2;
        continue;
      }
      if (ch === DOLLAR) {
        if (doubled) {
          if (state.src.charCodeAt(pos + 1) === DOLLAR) {
            end = pos;
            break;
          }
          pos += 1;
          continue;
        }
        end = pos;
        break;
      }
      pos += 1;
    }
    if (end === -1) return false;

    const after = state.src.charCodeAt(end + markerLength);
    if (!Number.isNaN(after) && after >= 0x30 && after <= 0x39) return false;

    const content = state.src.slice(start + markerLength, end);
    if (content.trim() === "") return false;
    if (/\s$/.test(content)) return false;

    if (!silent) {
      const token = state.push("math_inline", "span", 0);
      token.markup = doubled ? "$$" : "$";
      token.content = content;
    }
    state.pos = end + markerLength;
    return true;
  }

  md.inline.ruler.before("escape", "math_inline", rule);
}

const CUSTOM_EMOJI_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A known shortcode, or the UUID of a custom workspace emoji. An unknown
 * shortcode is not an emoji and stays literal text.
 */
function isEmojiName(name: string): boolean {
  return Object.hasOwn(EMOJI_SHORTCODES, name) || CUSTOM_EMOJI_ID.test(name);
}

/** `:shortcode:` and `:<uuid>:` custom emoji. */
export function emojiPlugin(md: MarkdownIt): void {
  function rule(state: StateInline, silent: boolean): boolean {
    const start = state.pos;
    if (state.src.charCodeAt(start) !== COLON) return false;
    // A colon glued to a word is punctuation, not an emoji delimiter.
    if (start > 0 && isAlphaNumeric(state.src.charCodeAt(start - 1))) return false;

    const match = /^:([a-zA-Z0-9_+-]{1,64}):/.exec(state.src.slice(start, state.posMax));
    const name = match?.[1];
    if (!name || !isEmojiName(name)) return false;

    if (!silent) {
      const token = state.push("emoji", "span", 0);
      token.markup = ":";
      token.content = name;
    }
    state.pos = start + name.length + 2;
    return true;
  }

  md.inline.ruler.before("escape", "emoji", rule);
}

/**
 * A container fence such as `:::info` … `:::` or `+++` … `+++`. The closing
 * marker run must be exactly as long as the opening one, which is what makes
 * the toggle dialect's "nest with one more plus" convention work.
 */
export function containerPlugin(md: MarkdownIt, name: string, marker: string): void {
  const code = marker.charCodeAt(0);
  const minLength = 3;

  function markerRun(state: StateBlock, line: number): { length: number; rest: string } | null {
    const start = state.bMarks[line] ?? 0;
    const shift = state.tShift[line] ?? 0;
    const max = state.eMarks[line] ?? 0;
    if ((state.sCount[line] ?? 0) - (state.blkIndent ?? 0) >= 4) return null;
    let pos = start + shift;
    if (pos >= max) return null;
    if (state.src.charCodeAt(pos) !== code) return null;
    const from = pos;
    while (pos < max && state.src.charCodeAt(pos) === code) pos += 1;
    const length = pos - from;
    if (length < minLength) return null;
    return { length, rest: state.src.slice(pos, max).trim() };
  }

  function rule(state: StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
    const open = markerRun(state, startLine);
    if (!open) return false;
    if (silent) return true;

    let closeLine = -1;
    for (let line = startLine + 1; line < endLine; line += 1) {
      const candidate = markerRun(state, line);
      if (candidate?.length === open.length && candidate.rest === "") {
        closeLine = line;
        break;
      }
    }
    const contentEnd = closeLine === -1 ? endLine : closeLine;

    const openToken = state.push(`${name}_open`, "div", 1);
    openToken.markup = marker.repeat(open.length);
    openToken.info = open.rest;
    openToken.map = [startLine, contentEnd];

    const oldParent = state.parentType;
    const oldLineMax = state.lineMax;
    state.lineMax = contentEnd;
    state.md.block.tokenize(state, startLine + 1, contentEnd);
    state.lineMax = oldLineMax;
    state.parentType = oldParent;

    const closeToken = state.push(`${name}_close`, "div", -1);
    closeToken.markup = marker.repeat(open.length);

    state.line = closeLine === -1 ? endLine : closeLine + 1;
    return true;
  }

  md.block.ruler.before("fence", name, rule, {
    alt: ["paragraph", "reference", "blockquote", "list"],
  });
}

/** `$$` block math. */
export function mathBlockPlugin(md: MarkdownIt): void {
  function rule(state: StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
    const start = (state.bMarks[startLine] ?? 0) + (state.tShift[startLine] ?? 0);
    const max = state.eMarks[startLine] ?? 0;
    if ((state.sCount[startLine] ?? 0) - (state.blkIndent ?? 0) >= 4) return false;
    if (state.src.charCodeAt(start) !== DOLLAR) return false;
    if (state.src.charCodeAt(start + 1) !== DOLLAR) return false;

    const firstLine = state.src.slice(start + 2, max);
    if (silent) return true;

    // Single-line form: `$$ x $$`.
    if (firstLine.trimEnd().endsWith("$$")) {
      const inner = firstLine.trimEnd().slice(0, -2);
      const token = state.push("math_block", "div", 0);
      token.markup = "$$";
      token.content = inner.trim();
      token.map = [startLine, startLine + 1];
      state.line = startLine + 1;
      return true;
    }

    const lines: string[] = [];
    if (firstLine.trim() !== "") lines.push(firstLine.trim());
    let line = startLine + 1;
    let closed = false;
    for (; line < endLine; line += 1) {
      const lineStart = (state.bMarks[line] ?? 0) + (state.tShift[line] ?? 0);
      const lineMax = state.eMarks[line] ?? 0;
      const text = state.src.slice(lineStart, lineMax);
      if (text.trim() === "$$") {
        closed = true;
        break;
      }
      lines.push(state.src.slice(state.bMarks[line] ?? 0, lineMax));
    }

    const token = state.push("math_block", "div", 0);
    token.markup = "$$";
    token.content = lines.join("\n");
    token.map = [startLine, line];
    state.line = closed ? line + 1 : line;
    return true;
  }

  md.block.ruler.before("fence", "math_block", rule, {
    alt: ["paragraph", "reference", "blockquote", "list"],
  });
}

/**
 * `__text__` means underline in this dialect, not strong. markdown-it records
 * the delimiter it matched, so the retag is a straight token rewrite.
 */
export function underlinePlugin(md: MarkdownIt): void {
  function retag(tokens: Token[]): void {
    for (const token of tokens) {
      if (token.children) retag(token.children);
      if (token.markup !== "__") continue;
      if (token.type === "strong_open") {
        token.type = "underline_open";
        token.tag = "u";
      } else if (token.type === "strong_close") {
        token.type = "underline_close";
        token.tag = "u";
      }
    }
  }

  md.core.ruler.after("inline", "underline", (state: StateCore) => {
    retag(state.tokens);
    return true;
  });
}

const MENTION_HREF = /^mention:\/\/([^/]+)\/([^/]+)\/(.*)$/;
const ABSOLUTE_HREF = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/**
 * Mention types the markdown parser will promote. External-service mentions
 * (`issue`, `pull_request`, `project`) reach a document through an integration,
 * never through markdown, so a `mention://…/issue/…` link stays an `@` and an
 * ordinary link.
 */
const PARSEABLE_MENTION_TYPES = new Set(["user", "group", "document", "collection", "date"]);

/** Stands in for an escaped backslash so it cannot form a `\n` hard break. */
export const ESCAPED_BACKSLASH = "\uE001";

/**
 * markdown-it unescapes `\\` into a bare backslash, which would then pair with
 * a following `n` and read as a hard break. Claiming the pair first keeps the
 * two spellings distinguishable.
 */
export function escapedBackslashPlugin(md: MarkdownIt): void {
  md.inline.ruler.before("escape", "escaped_backslash", (state: StateInline, silent: boolean) => {
    if (state.src.charCodeAt(state.pos) !== BACKSLASH) return false;
    if (state.src.charCodeAt(state.pos + 1) !== BACKSLASH) return false;
    if (!silent) state.pending += ESCAPED_BACKSLASH;
    state.pos += 2;
    return true;
  });
}

/**
 * A link immediately preceded by `@` collapses into a mention node. A
 * `mention://` href carries the ids; any other href becomes a `url` mention
 * with freshly generated ids.
 */
export function mentionPlugin(md: MarkdownIt, generateId: () => string): void {
  function convert(children: Token[], TokenCtor: typeof Token): Token[] {
    const out: Token[] = [];
    for (let i = 0; i < children.length; i += 1) {
      const token = children[i];
      if (!token) continue;
      const previous = out[out.length - 1];
      if (
        token.type === "link_open" &&
        previous?.type === "text" &&
        previous.content.endsWith("@")
      ) {
        let depth = 1;
        let close = i + 1;
        const labelParts: string[] = [];
        while (close < children.length && depth > 0) {
          const inner = children[close];
          if (!inner) break;
          if (inner.type === "link_open") depth += 1;
          else if (inner.type === "link_close") {
            depth -= 1;
            if (depth === 0) break;
          } else labelParts.push(inner.content);
          close += 1;
        }
        const href = String(token.attrGet("href") ?? "");
        const label = labelParts.join("");
        const match = MENTION_HREF.exec(href);
        if (match && !PARSEABLE_MENTION_TYPES.has(match[2] ?? "")) {
          out.push(token);
          continue;
        }
        if (!match && !ABSOLUTE_HREF.test(href)) {
          // An `@` before a relative link is literal text, not a mention.
          out.push(token);
          continue;
        }
        const mention = new TokenCtor("mention", "span", 0);
        if (match) {
          mention.attrSet("mentionId", match[1] ?? "");
          mention.attrSet("mentionType", match[2] ?? "user");
          mention.attrSet("modelId", match[3] ?? "");
        } else {
          mention.attrSet("mentionId", generateId());
          mention.attrSet("mentionType", "url");
          mention.attrSet("modelId", generateId());
          mention.attrSet("href", href);
        }
        mention.content = label;
        previous.content = previous.content.slice(0, -1);
        if (previous.content === "") out.pop();
        out.push(mention);
        i = close;
        continue;
      }
      out.push(token);
    }
    return out;
  }

  md.core.ruler.after("inline", "mention", (state: StateCore) => {
    for (const token of state.tokens) {
      if (token.type === "inline" && token.children) {
        token.children = convert(token.children, state.Token);
      }
    }
    return true;
  });
}
